import type { CriteriaItem } from './building-design-criteria';

export interface BuildingDesignCriteria {
  winter_external_dry_bulb: number;
  summer_external_dry_bulb: number;
  summer_external_wet_bulb: number;
  wall_u_value: number;
  window_u_value: number;
  door_u_value: number;
  roof_u_value: number;
  ground_u_value: number;
  window_g_value: number;
  air_tightness: number;
  heating_base_diversity: number;
  electrical_base_diversity: number;
  mains_water_temp: number;
  dhw_delivery_temp: number;
  electrical_power_factor: number;
  heat_recovery_factor: number;
  foul_drainage_velocity: number;
  daily_hot_water: number;
  daily_cold_water: number;
  sensible_load_per_person: number;
  latent_load_per_person: number;
}

export interface BuildingJsonData {
  building_package_id: string;
  building_transform: {
    rotation_angle: number;
    rotation_origin: unknown;
    translation_vector: unknown;
  };
  building_design_criteria: BuildingDesignCriteria;
  client_name: string;
  country: string;
  custom_export_settings: unknown;
  floors: unknown[];
  notes: string;
  parking_settings: unknown;
  project_name: string;
  qa_checklist: unknown;
  risers: unknown[];
  default_room_criteria_map: unknown;
  routing_settings: unknown;
  slabs: unknown[];
  total_electrical_load: number;
  total_extract_air: number;
  total_heat_load: number;
  total_occupancy: number;
  total_supply_air: number;
  uuid: string;
}

export interface CriteriaConfig<
  K extends keyof BuildingDesignCriteria = keyof BuildingDesignCriteria,
> {
  id: string;
  propertyKey: K;
  labelKey: string;
  unit: string;
  toDisplay?: (value: BuildingDesignCriteria[K]) => number;
  fromDisplay?: (value: number) => BuildingDesignCriteria[K];
  isNumeric?: boolean;
}

export const CRITERIA_CONFIGS: CriteriaConfig[] = [
  {
    id: 'winter_external_dry_bulb',
    propertyKey: 'winter_external_dry_bulb',
    labelKey: 'building-design-criteria.criteria.winter-external-dry-bulb',
    unit: '°C',
    isNumeric: true,
  },
  {
    id: 'summer_external_dry_bulb',
    propertyKey: 'summer_external_dry_bulb',
    labelKey: 'building-design-criteria.criteria.summer-external-dry-bulb',
    unit: '°C',
    isNumeric: true,
  },
  {
    id: 'summer_external_wet_bulb',
    propertyKey: 'summer_external_wet_bulb',
    labelKey: 'building-design-criteria.criteria.summer-external-wet-bulb',
    unit: '°C',
    isNumeric: true,
  },
  {
    id: 'wall_u_value',
    propertyKey: 'wall_u_value',
    labelKey: 'building-design-criteria.criteria.wall-u-value',
    unit: 'W/m²K',
    isNumeric: true,
  },
  {
    id: 'window_u_value',
    propertyKey: 'window_u_value',
    labelKey: 'building-design-criteria.criteria.window-u-value',
    unit: 'W/m²K',
    isNumeric: true,
  },
  {
    id: 'door_u_value',
    propertyKey: 'door_u_value',
    labelKey: 'building-design-criteria.criteria.door-u-value',
    unit: 'W/m²K',
    isNumeric: true,
  },
  {
    id: 'roof_u_value',
    propertyKey: 'roof_u_value',
    labelKey: 'building-design-criteria.criteria.roof-u-value',
    unit: 'W/m²K',
    isNumeric: true,
  },
  {
    id: 'ground_u_value',
    propertyKey: 'ground_u_value',
    labelKey: 'building-design-criteria.criteria.ground-u-value',
    unit: 'W/m²K',
    isNumeric: true,
  },
  {
    id: 'window_g_value',
    propertyKey: 'window_g_value',
    labelKey: 'building-design-criteria.criteria.window-g-value',
    unit: 'G',
    isNumeric: true,
  },
  {
    id: 'air_tightness',
    propertyKey: 'air_tightness',
    labelKey: 'building-design-criteria.criteria.air-tightness',
    unit: 'm³/m²·h@50Pa',
    isNumeric: true,
  },
  {
    id: 'heating_base_diversity',
    propertyKey: 'heating_base_diversity',
    labelKey: 'building-design-criteria.criteria.heating-base-diversity',
    unit: '%',
    toDisplay: (value) => (value as number) * 100,
    fromDisplay: (value) => (value / 100) as number,
    isNumeric: true,
  },
  {
    id: 'electrical_base_diversity',
    propertyKey: 'electrical_base_diversity',
    labelKey: 'building-design-criteria.criteria.electrical-base-diversity',
    unit: '%',
    toDisplay: (value) => (value as number) * 100,
    fromDisplay: (value) => (value / 100) as number,
    isNumeric: true,
  },
  {
    id: 'mains_water_temp',
    propertyKey: 'mains_water_temp',
    labelKey: 'building-design-criteria.criteria.mains-water-temperature',
    unit: '°C',
    isNumeric: true,
  },
  {
    id: 'dhw_delivery_temp',
    propertyKey: 'dhw_delivery_temp',
    labelKey: 'building-design-criteria.criteria.dhw-delivery-temperature',
    unit: '°C',
    isNumeric: true,
  },
  {
    id: 'electrical_power_factor',
    propertyKey: 'electrical_power_factor',
    labelKey: 'building-design-criteria.criteria.electrical-power-factor',
    unit: 'PF',
    isNumeric: true,
  },
  {
    id: 'heat_recovery_factor',
    propertyKey: 'heat_recovery_factor',
    labelKey: 'building-design-criteria.criteria.ventilation-heat-recovery-efficiency',
    unit: '%',
    toDisplay: (value) => (value as number) * 100,
    fromDisplay: (value) => (value / 100) as number,
    isNumeric: true,
  },
  {
    id: 'foul_drainage_velocity',
    propertyKey: 'foul_drainage_velocity',
    labelKey: 'building-design-criteria.criteria.foul-drainage-velocity',
    unit: 'm/s',
    isNumeric: true,
  },
  {
    id: 'daily_hot_water',
    propertyKey: 'daily_hot_water',
    labelKey: 'building-design-criteria.criteria.daily-hot-water',
    unit: 'L/pers./day',
    isNumeric: true,
  },
  {
    id: 'daily_cold_water',
    propertyKey: 'daily_cold_water',
    labelKey: 'building-design-criteria.criteria.daily-cold-water',
    unit: 'L/pers./day',
    isNumeric: true,
  },
  {
    id: 'sensible_load_per_person',
    propertyKey: 'sensible_load_per_person',
    labelKey: 'building-design-criteria.criteria.sensible-load-per-person',
    unit: 'kW/person',
    isNumeric: true,
  },
  {
    id: 'latent_load_per_person',
    propertyKey: 'latent_load_per_person',
    labelKey: 'building-design-criteria.criteria.latent-load-per-person',
    unit: 'kW/person',
    isNumeric: true,
  },
];

export const getCriteriaFromBuildingData = (
  designCriteria: BuildingDesignCriteria,
  t: (key: string) => string,
): CriteriaItem[] => {
  return CRITERIA_CONFIGS.map((config) => {
    const rawValue = designCriteria[config.propertyKey] as number;
    const displayValue = config.toDisplay ? config.toDisplay(rawValue) : rawValue;

    return {
      id: config.id,
      label: t(config.labelKey),
      value: String(displayValue),
      unit: config.unit,
    };
  });
};
